import { useMemo, useState } from 'react';
import type { Skill } from '@teentack/shared';
import { CheckIcon, CloseIcon, SearchIcon } from '@/components/ui/Icons';

/** Matches the cap on `skillIds` in the shared register schema. */
export const MAX_SKILLS = 20;

const ALL = '__all__';

function matches(skill: Skill, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return (
    skill.name.toLowerCase().includes(needle) ||
    (skill.category ?? '').toLowerCase().includes(needle)
  );
}

/**
 * The signup skill multi-select.
 *
 * The catalogue is long, so the panel does three things at once: a search box
 * for people who know what they want, category filters for people browsing,
 * and a summary of what is already picked so the choice never scrolls out of
 * sight.
 */
export function SkillPicker({
  skills,
  selected,
  onChange,
  loading,
  failed,
}: {
  skills: Skill[];
  selected: string[];
  onChange: (next: string[]) => void;
  loading?: boolean;
  failed?: boolean;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>(ALL);

  const atCap = selected.length >= MAX_SKILLS;

  const byId = useMemo(() => new Map(skills.map((skill) => [skill.publicId, skill])), [skills]);

  /** Category tabs, each carrying how many of its skills are already picked. */
  const categories = useMemo(() => {
    const counts = new Map<string, { total: number; picked: number }>();
    for (const skill of skills) {
      const key = skill.category ?? 'Other';
      const entry = counts.get(key) ?? { total: 0, picked: 0 };
      entry.total += 1;
      if (selected.includes(skill.publicId)) entry.picked += 1;
      counts.set(key, entry);
    }
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [skills, selected]);

  /** Visible skills, grouped by category and filtered by search + tab. */
  const groups = useMemo(() => {
    const grouped = new Map<string, Skill[]>();
    for (const skill of skills) {
      const key = skill.category ?? 'Other';
      if (category !== ALL && key !== category) continue;
      if (!matches(skill, query)) continue;
      grouped.set(key, [...(grouped.get(key) ?? []), skill]);
    }
    return [...grouped.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [skills, category, query]);

  const visibleCount = groups.reduce((total, [, entries]) => total + entries.length, 0);

  function toggle(publicId: string): void {
    if (selected.includes(publicId)) {
      onChange(selected.filter((id) => id !== publicId));
    } else if (!atCap) {
      onChange([...selected, publicId]);
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-line bg-white">
      {/* Header: label + live count */}
      <div className="flex items-baseline justify-between gap-3 px-4 pt-4">
        <span className="text-sm font-semibold text-ink">Skills</span>
        <span
          className={`text-xs font-semibold ${atCap ? 'text-sun-700' : 'text-slate-400'}`}
          aria-live="polite"
        >
          {selected.length > 0 ? `${selected.length} of ${MAX_SKILLS} picked` : 'Optional'}
        </span>
      </div>

      {/* What is already picked, removable in place */}
      {selected.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-b border-line bg-brand-50/50 px-4 py-3">
          {selected.map((id) => {
            const skill = byId.get(id);
            if (!skill) return null;
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 py-1 pr-1.5 pl-3 text-xs font-semibold text-white shadow-soft"
              >
                {skill.name}
                <button
                  type="button"
                  onClick={() => toggle(id)}
                  aria-label={`Remove ${skill.name}`}
                  className="grid size-4 place-items-center rounded-full bg-white/20 transition hover:bg-white/35"
                >
                  <CloseIcon className="size-2.5" />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={() => onChange([])}
            className="ml-auto text-xs font-semibold text-slate-500 underline-offset-2 hover:text-ink hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            // This box filters the list; it is not a form field. Without this,
            // Enter would submit the wizard and skip to the next step.
            onKeyDown={(event) => {
              if (event.key === 'Enter') event.preventDefault();
            }}
            placeholder="Search skills — babysitting, tutoring, lawn…"
            aria-label="Search skills"
            className="w-full rounded-xl border border-line bg-canvas py-2 pr-3 pl-9 text-sm text-ink transition outline-none placeholder:text-slate-400 focus:border-brand-500 focus:bg-white focus:ring-4 focus:ring-brand-500/15"
          />
        </div>
      </div>

      {/* Category filters */}
      {categories.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto px-4 pb-1">
          <FilterTab
            active={category === ALL}
            onClick={() => setCategory(ALL)}
            label="All"
            total={skills.length}
            picked={selected.length}
          />
          {categories.map(([name, count]) => (
            <FilterTab
              key={name}
              active={category === name}
              onClick={() => setCategory(name)}
              label={name}
              total={count.total}
              picked={count.picked}
            />
          ))}
        </div>
      )}

      {/* The catalogue */}
      <div className="max-h-72 space-y-5 overflow-y-auto px-4 py-4">
        {loading && <p className="text-sm text-slate-500">Loading the skills catalogue…</p>}

        {failed && (
          <p className="text-sm text-slate-500">
            The skills list could not be loaded. You can add skills later from your profile.
          </p>
        )}

        {!loading && !failed && visibleCount === 0 && (
          <p className="text-sm text-slate-500">
            No skills match “{query}”. Try a different word, or pick a category above.
          </p>
        )}

        {groups.map(([name, entries]) => (
          <div key={name}>
            <p className="mb-2 text-xs font-bold tracking-wide text-slate-400 uppercase">{name}</p>
            <div className="flex flex-wrap gap-2">
              {entries.map((skill) => {
                const isSelected = selected.includes(skill.publicId);
                return (
                  <button
                    key={skill.publicId}
                    type="button"
                    onClick={() => toggle(skill.publicId)}
                    disabled={!isSelected && atCap}
                    aria-pressed={isSelected}
                    className={`inline-flex items-center gap-1.5 rounded-full border py-1.5 text-xs font-semibold transition duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-40 ${
                      isSelected
                        ? 'border-brand-600 bg-brand-600 pr-3.5 pl-2.5 text-white shadow-soft'
                        : 'border-line bg-white px-3.5 text-slate-600 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700'
                    }`}
                  >
                    {isSelected && <CheckIcon className="size-3" />}
                    {skill.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {atCap && (
        <p className="border-t border-line bg-sun-50 px-4 py-2.5 text-xs font-medium text-sun-700">
          That is the maximum of {MAX_SKILLS} skills. Remove one to add another.
        </p>
      )}
    </div>
  );
}

function FilterTab({
  active,
  onClick,
  label,
  total,
  picked,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  total: number;
  picked: number;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold whitespace-nowrap transition duration-200 ease-out ${
        active ? 'bg-ink text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
      }`}
    >
      {label}
      <span
        className={`rounded px-1 text-[0.65rem] tabular-nums ${
          picked > 0
            ? 'bg-safe-500 text-white'
            : active
              ? 'bg-white/20 text-white'
              : 'bg-white text-slate-400'
        }`}
      >
        {picked > 0 ? picked : total}
      </span>
    </button>
  );
}
